/**
 * Cookie utility functions for secure token management
 * This will be used when migrating to httpOnly cookies
 */

// Set a secure cookie
export const setSecureCookie = (name, value, options = {}) => {
  const defaultOptions = {
    path: '/',
    secure: true, // Only send over HTTPS
    sameSite: 'strict', // Protect against CSRF
    httpOnly: false, // Will be true when backend supports it
    maxAge: 60 * 60 * 24 * 7, // 7 days default
    ...options
  };

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  if (defaultOptions.path) cookieString += `; path=${defaultOptions.path}`;
  if (defaultOptions.domain) cookieString += `; domain=${defaultOptions.domain}`;
  if (defaultOptions.maxAge) cookieString += `; max-age=${defaultOptions.maxAge}`;
  if (defaultOptions.secure) cookieString += '; secure';
  if (defaultOptions.sameSite) cookieString += `; samesite=${defaultOptions.sameSite}`;
  
  document.cookie = cookieString;
};

// Get a cookie value
export const getCookie = (name) => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
};

// Delete a cookie
export const deleteCookie = (name, options = {}) => {
  const defaultOptions = {
    path: '/',
    domain: options.domain,
    ...options
  };
  
  setSecureCookie(name, '', { ...defaultOptions, maxAge: -1 });
};

// Check if cookies are supported
export const areCookiesSupported = () => {
  try {
    document.cookie = 'test=1';
    const supported = document.cookie.indexOf('test=') !== -1;
    document.cookie = 'test=1; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    return supported;
  } catch (e) {
    return false;
  }
};

// Set authentication cookies
export const setAuthCookies = (accessToken, refreshToken, options = {}) => {
  const cookieOptions = {
    maxAge: 60 * 60 * 24 * 7, // 7 days for refresh token
    ...options
  };

  // Set access token cookie (shorter expiry)
  setSecureCookie('accessToken', accessToken, {
    ...cookieOptions,
    maxAge: 60 * 60, // 1 hour for access token
  });

  // Set refresh token cookie (longer expiry)
  setSecureCookie('refreshToken', refreshToken, cookieOptions);
};

// Clear authentication cookies
export const clearAuthCookies = () => {
  deleteCookie('accessToken');
  deleteCookie('refreshToken');
};

// Get authentication tokens from cookies
export const getAuthTokens = () => {
  return {
    accessToken: getCookie('accessToken'),
    refreshToken: getCookie('refreshToken')
  };
};

// Check if auth cookies exist
export const hasAuthCookies = () => {
  const tokens = getAuthTokens();
  return !!(tokens.accessToken && tokens.refreshToken);
};

